import { useEffect, useState } from "react";
import {
  getOrders,
  markDelivered,
  deleteOrder,
  updateOrder,
  markPaid
} from "../apis/orders";

import { getMenu } from "../apis/menu";

export default function Orders() {

  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);

  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editItems, setEditItems] = useState([]);

  useEffect(() => {
    loadOrders();
    loadMenu();
  }, []);

  // Format: Friday
  const getDayName = (dateStr) => {
    if (!dateStr) return "";

    const [date] = dateStr.split("T");
    const [year, month, day] = date.split("-");

    return new Date(year, month - 1, day)
      .toLocaleDateString("en-US", { weekday: "long" });
  };
  const formatTime = (dateTime) => {
    if (!dateTime) return "";
    return dateTime.split("T")[1]?.slice(0, 5);
  };

  // Format: DD/MM/YYYY
  const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const [date] = dateStr.split("T");
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  const loadOrders = async () => {
    try {

      setLoading(true);

      const res = await getOrders();

      const filteredSorted =
        (res.data || [])
          .filter(order => !order.paid)
          .sort((a, b) =>
            a.deliverBy.localeCompare(b.deliverBy)
          );

      setOrders(filteredSorted);

    } catch {

      alert("Failed to load orders");

    } finally {

      setLoading(false);

    }
  };

  const loadMenu = async () => {
    try {
      const res = await getMenu();
      setMenu(res.data || []);
    } catch {
      alert("Failed to load menu");
    }
  };

  // DELIVERY
  const deliver = async (id) => {
    try {

      setWorkingId(id);

      await markDelivered(id);

      loadOrders();

    } catch {

      alert("Failed");

    } finally {

      setWorkingId(null);

    }
  };

  // PAYMENT
  const pay = async (id) => {
    try {

      setWorkingId(id);

      await markPaid(id);

      // Remove immediately from UI
      setOrders(prev =>
        prev.filter(order => order.id !== id)
      );

    } catch {

      alert("Failed");

    } finally {

      setWorkingId(null);

    }
  };

  // DELETE
  const remove = async (id) => {

    if (!window.confirm("Delete order?"))
      return;

    try {

      setWorkingId(id);

      await deleteOrder(id);

      loadOrders();

    } catch {

      alert("Failed");

    } finally {

      setWorkingId(null);

    }
  };

  // START EDIT
  const startEdit = (order) => {

    setEditingId(order.id);

    setEditItems(
      order.items.map(item => ({
        menuItemId: item.menuItemId,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      }))
    );
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  // INCREASE QTY
  const increaseQty = (menuItem) => {

    const exists =
      editItems.find(
        i =>
          i.menuItemId === menuItem.id
      );

    if (exists) {

      setEditItems(
        editItems.map(i =>
          i.menuItemId === menuItem.id
            ? {
              ...i,
              quantity:
                i.quantity + 1
            }
            : i
        )
      );

    } else {

      setEditItems([
        ...editItems,
        {
          menuItemId: menuItem.id,
          name: menuItem.name,
          price: menuItem.price,
          quantity: 1
        }
      ]);

    }
  };

  // DECREASE QTY
  const decreaseQty = (menuItemId) => {

    const item =
      editItems.find(
        i =>
          i.menuItemId === menuItemId
      );

    if (!item) return;

    if (item.quantity === 1) {

      setEditItems(
        editItems.filter(
          i =>
            i.menuItemId !==
            menuItemId
        )
      );

    } else {

      setEditItems(
        editItems.map(i =>
          i.menuItemId ===
            menuItemId
            ? {
              ...i,
              quantity:
                i.quantity - 1
            }
            : i
        )
      );

    }
  };

  // SAVE EDIT
  const saveEdit = async (order) => {

    try {

      setWorkingId(order.id);

      await updateOrder(order.id, {
        ...order,
        items: editItems
      });

      setEditingId(null);

      loadOrders();

    } catch {

      alert("Failed to update");

    } finally {

      setWorkingId(null);

    }
  };

  if (loading)
    return (
      <div className="p-4">
        Loading...
      </div>
    );

  return (

    <div className="p-4 space-y-4">

      {orders.map(order => {

        const isEditing =
          editingId === order.id;

        const itemsToShow =
          isEditing
            ? editItems
            : order.items;

        const total =
          itemsToShow.reduce(
            (sum, i) =>
              sum +
              i.price *
              i.quantity,
            0
          );

        return (

          <div
            key={order.id}
            className="border rounded-xl p-4 bg-white shadow"
          >

            {/* CUSTOMER */}
            <div className="text-lg font-bold">
              {order.customerName}
            </div>

            {/* DELIVERY DAY */}
            <div className="text-sm text-gray-600 mt-1">
              Order for:{" "}
              <span className="font-semibold">
                {getDayName(order.deliverBy)}
              </span>{" "}
              (
              {formatDateDDMMYYYY(order.deliverBy)} at {formatTime(order.deliverBy)}
              )
            </div>

            {/* STATUS */}
            <div className="flex gap-4 mt-1 text-sm">

              <span className={
                order.delivered
                  ? "text-green-600"
                  : "text-orange-600"
              }>
                Delivery:
                {order.delivered
                  ? " Delivered"
                  : " Pending"}
              </span>

              <span className={
                order.paid
                  ? "text-green-600"
                  : "text-red-600"
              }>
                Payment:
                {order.paid
                  ? " Paid"
                  : " Pending"}
              </span>

            </div>

            {/* ITEMS */}
            <div className="mt-3 space-y-2">

              {(isEditing
                ? menu
                : order.items
              ).map(item => {

                const menuItem =
                  isEditing
                    ? item
                    : null;

                const editItem =
                  isEditing
                    ? editItems.find(
                      i =>
                        i.menuItemId ===
                        menuItem.id
                    )
                    : item;

                const qty =
                  isEditing
                    ? editItem
                      ?.quantity || 0
                    : item.quantity;

                const name =
                  isEditing
                    ? menuItem.name
                    : item.name;

                const price =
                  isEditing
                    ? menuItem.price
                    : item.price;

                return (

                  <div
                    key={
                      isEditing
                        ? menuItem.id
                        : item.menuItemId
                    }
                    className="flex justify-between items-center"
                  >

                    <span>
                      {name}
                    </span>

                    {isEditing ? (

                      <div className="flex gap-2">

                        <button
                          onClick={() =>
                            decreaseQty(
                              menuItem.id
                            )
                          }
                          className="bg-red-500 text-white px-2 rounded"
                        >
                          −
                        </button>

                        <span>
                          {qty}
                        </span>

                        <button
                          onClick={() =>
                            increaseQty(
                              menuItem
                            )
                          }
                          className="bg-green-600 text-white px-2 rounded"
                        >
                          +
                        </button>

                      </div>

                    ) : (

                      <span>
                        {qty} × SAR{" "}
                        {price}
                      </span>

                    )}

                  </div>

                );

              })}

            </div>

            {/* TOTAL */}
            <div className="mt-3 font-semibold">
              Total: SAR {total}
            </div>

            {/* ACTIONS */}
            <div className="flex flex-wrap gap-2 mt-3">

              {!order.delivered &&
                !isEditing && (

                  <>
                    <button
                      disabled={
                        workingId ===
                        order.id
                      }
                      onClick={() =>
                        deliver(
                          order.id
                        )
                      }
                      className="bg-green-600 text-white px-4 py-2 rounded"
                    >
                      Delivered
                    </button>

                    <button
                      onClick={() =>
                        startEdit(order)
                      }
                      className="bg-blue-600 text-white px-3 py-1 rounded"
                    >
                      Edit
                    </button>

                    <button
                      disabled={
                        workingId ===
                        order.id
                      }
                      onClick={() =>
                        remove(
                          order.id
                        )
                      }
                      className="bg-red-600 text-white px-3 py-1 rounded"
                    >
                      Delete
                    </button>

                  </>
                )}

              {order.delivered &&
                !isEditing && (

                  <button
                    disabled={
                      workingId ===
                      order.id
                    }
                    onClick={() =>
                      pay(order.id)
                    }
                    className="bg-purple-600 text-white px-3 py-1 rounded"
                  >
                    Mark Paid
                  </button>

                )}

              {isEditing && (

                <>
                  <button
                    disabled={
                      workingId ===
                      order.id
                    }
                    onClick={() =>
                      saveEdit(order)
                    }
                    className="bg-green-600 text-white px-3 py-1 rounded"
                  >
                    Save
                  </button>

                  <button
                    onClick={
                      cancelEdit
                    }
                    className="bg-gray-500 text-white px-3 py-1 rounded"
                  >
                    Cancel
                  </button>
                </>

              )}

            </div>

          </div>

        );

      })}

    </div>

  );

}